import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Save, Loader2, AlertTriangle, Shield, Layers } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { ApprovalLevelCard } from '@/components/tms/ApprovalLevelCard';
import { ApprovalActionsConfig } from '@/components/tms/ApprovalActionsConfig';
import {
  rfqApi,
  FreightRFQ,
  ApprovalLevel,
  ApprovalFlowType,
  ApproverCandidate,
  ApprovalAction,
  CreateApprovalConfigDto,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { stagger, fadeUp } from '@/lib/animations';

export default function FreightRFQApprovalConfig() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rfq, setRfq] = useState<FreightRFQ | null>(null);
  const [approverCandidates, setApproverCandidates] = useState<ApproverCandidate[]>([]);

  // Config state
  const [isEnabled, setIsEnabled] = useState(true);
  const [flowType, setFlowType] = useState<ApprovalFlowType>('Sequential');
  const [levels, setLevels] = useState<ApprovalLevel[]>([]);
  const [approvalActions, setApprovalActions] = useState<ApprovalAction[]>([]);
  const [rejectionActions, setRejectionActions] = useState<ApprovalAction[]>([]);
  const loadData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [rfqData, configData, candidates] = await Promise.all([
        rfqApi.getById(id),
        rfqApi.getApprovalConfig(id),
        rfqApi.getApproverCandidates(),
      ]);

      setRfq(rfqData);
      setApproverCandidates(candidates);

      if (configData) {
        setIsEnabled(configData.isEnabled);
        setFlowType(configData.flowType);
        setLevels(configData.levels);
        setApprovalActions(configData.onApprovalActions || []);
        setRejectionActions(configData.onRejectionActions || []);
      } else {
        // Default config
        setLevels([createDefaultLevel(1)]);
        setApprovalActions(getDefaultApprovalActions());
        setRejectionActions(getDefaultRejectionActions());
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  const createDefaultLevel = (levelNumber: number): ApprovalLevel => ({
    levelNumber,
    name: `Approval Level ${levelNumber}`,
    logic: 'Anyone',
    approvers: [],
    approverIds: [],
    rules: [],
  });

  const getDefaultApprovalActions = (): ApprovalAction[] => [
    { type: 'UpdateRFQStatus', status: 'Awarded' },
    { type: 'NotifyCarrier' },
    { type: 'LogEvent' },
  ];

  const getDefaultRejectionActions = (): ApprovalAction[] => [
    { type: 'ResetAwardStatus' },
    { type: 'SendNotification', template: 'rejection-notification' },
    { type: 'LogEvent' },
  ];

  const handleAddLevel = () => {
    if (levels.length >= 5) {
      toast.error('Maximum 5 approval levels allowed');
      return;
    }
    const newLevel = createDefaultLevel(levels.length + 1);
    setLevels([...levels, newLevel]);
  };

  const handleUpdateLevel = (index: number, updatedLevel: ApprovalLevel) => {
    const updated = [...levels];
    updated[index] = updatedLevel;
    setLevels(updated);
  };

  const handleDeleteLevel = (index: number) => {
    const updated = levels.filter((_, i) => i !== index);
    // Renumber levels
    updated.forEach((level, i) => {
      level.levelNumber = i + 1;
    });
    setLevels(updated);
  };

  const handleSave = async () => {
    if (!id) return;

    // Validate
    if (levels.length === 0) {
      toast.error('At least one approval level is required');
      return;
    }

    for (const level of levels) {
      if (level.approvers.length === 0) {
        toast.error(`Level "${level.name}" must have at least one approver`);
        return;
      }
    }

    setSaving(true);
    try {
      const dto: CreateApprovalConfigDto = {
        isEnabled,
        flowType,
        levels: levels.map((level) => ({
          levelNumber: level.levelNumber,
          name: level.name,
          logic: level.logic,
          approverIds: level.approvers.map((a) => a.userId),
          rules: level.rules.map((r) => ({
            ruleType: r.ruleType,
            condition: r.condition,
          })),
        })),
        onApprovalActions: approvalActions,
        onRejectionActions: rejectionActions,
      };

      await rfqApi.createOrUpdateApprovalConfig(id, dto);
      toast.success('Approval configuration saved successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(`/tms/rfq/${id}`)}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to RFQ
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/tms/rfq')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to RFQs
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertTriangle className="w-12 h-12 text-danger-500 mb-4" />
          <h3 className="text-base font-semibold">RFQ Not Found</h3>
        </div>
      </div>
    );
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          title="RFQ Approval Config"
          description={`${rfq.rfqNumber} - ${rfq.title}`}
          actions={
            <div className="flex items-center gap-3">
              <Badge variant="outline">{rfq.status}</Badge>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* Enable/Disable Toggle */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">Approval Workflow</h3>
                  <p className="text-sm text-muted-foreground">
                    Require approval before finalizing awards
                  </p>
                </div>
              </div>
              <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {isEnabled && (
        <motion.div variants={fadeUp}>
          <Tabs defaultValue="levels" className="space-y-6">
            <TabsList>
              <TabsTrigger value="levels">Approval Levels</TabsTrigger>
              <TabsTrigger value="actions">Post-Approval Actions</TabsTrigger>
            </TabsList>

            <TabsContent value="levels" className="space-y-6">
              {/* Flow Type */}
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Workflow Type</h3>
                  <CardDescription>How approval levels are processed</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setFlowType('Sequential')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        flowType === 'Sequential'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex -space-x-1">
                          <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs">
                            1
                          </div>
                          <div className="w-6 h-6 rounded-full bg-primary-400 flex items-center justify-center text-white text-xs">
                            2
                          </div>
                          <div className="w-6 h-6 rounded-full bg-primary-300 flex items-center justify-center text-white text-xs">
                            3
                          </div>
                        </div>
                        <span className="font-medium">Sequential</span>
                      </div>
                      <p className="text-sm text-muted-foreground text-left">
                        Levels are approved one after another. Level 2 starts only after Level 1 is
                        complete.
                      </p>
                    </button>

                    <button
                      onClick={() => setFlowType('Parallel')}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        flowType === 'Parallel'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex gap-1">
                          <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs">
                            1
                          </div>
                          <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs">
                            2
                          </div>
                          <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs">
                            3
                          </div>
                        </div>
                        <span className="font-medium">Parallel</span>
                      </div>
                      <p className="text-sm text-muted-foreground text-left">
                        All levels are started at the same time. Approval is complete when all
                        levels approve.
                      </p>
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Approval Levels */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold">Approval Levels</h3>
                    <p className="text-sm text-muted-foreground">
                      Configure up to 5 approval levels
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleAddLevel} disabled={levels.length >= 5}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Level
                  </Button>
                </div>

                {levels.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Layers className="w-12 h-12 text-muted-foreground/50 mb-4" />
                      <h3 className="text-base font-semibold mb-2">No Approval Levels</h3>
                      <p className="text-muted-foreground mb-4">
                        Add at least one approval level to enable the workflow
                      </p>
                      <Button onClick={handleAddLevel}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Level
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {levels.map((level, index) => (
                      <ApprovalLevelCard
                        key={level.id || index}
                        level={level}
                        index={index}
                        approverCandidates={approverCandidates}
                        onUpdate={(updated) => handleUpdateLevel(index, updated)}
                        onDelete={() => handleDeleteLevel(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="actions">
              <ApprovalActionsConfig
                approvalActions={approvalActions}
                rejectionActions={rejectionActions}
                onApprovalActionsChange={setApprovalActions}
                onRejectionActionsChange={setRejectionActions}
              />
            </TabsContent>
          </Tabs>
        </motion.div>
      )}
    </motion.main>
  );
}
